import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Check } from 'lucide-react';
import { API_URL } from '../utils/api';

// Base questions everyone answers
const BASE_QUESTIONS = [
  {
    key: 'goal',
    title: "What's your main goal right now?",
    subtitle: 'We tune verdicts to what matters to you.',
    multi: false,
    options: [
      { value: 'lose_weight', label: 'Lose weight' },
      { value: 'build_muscle', label: 'Build muscle' },
      { value: 'eat_cleaner', label: 'Eat cleaner' },
      { value: 'manage_condition', label: 'Manage a health condition' },
      { value: 'feel_better', label: 'Just feel better' }
    ]
  },
  {
    key: 'avoid_ingredients',
    title: "Any ingredients you're trying to avoid?",
    subtitle: 'Pick any that apply.',
    multi: true,
    options: [
      { value: 'added_sugar', label: 'Added sugar' },
      { value: 'artificial_dyes', label: 'Artificial dyes' },
      { value: 'hfcs', label: 'High fructose corn syrup' },
      { value: 'msg', label: 'MSG' },
      { value: 'preservatives', label: 'Preservatives' },
      { value: 'all_of_above', label: 'All of the above' }
    ]
  },
  {
    key: 'eating_style',
    title: 'Do you follow any eating style?',
    subtitle: "We'll respect it on every verdict.",
    multi: false,
    options: [
      { value: 'keto', label: 'Keto' },
      { value: 'vegan', label: 'Vegan' },
      { value: 'vegetarian', label: 'Vegetarian' },
      { value: 'paleo', label: 'Paleo' },
      { value: 'gluten_free', label: 'Gluten-free' },
      { value: 'none', label: 'No specific diet' }
    ]
  },
  {
    key: 'allergies',
    title: 'Any allergies we should always flag?',
    subtitle: "We'll always treat these as red flags.",
    multi: true,
    options: [
      { value: 'nuts', label: 'Nuts' },
      { value: 'dairy', label: 'Dairy' },
      { value: 'gluten', label: 'Gluten' },
      { value: 'soy', label: 'Soy' },
      { value: 'eggs', label: 'Eggs' },
      { value: 'shellfish', label: 'Shellfish' },
      { value: 'none', label: 'None' }
    ]
  },
  {
    key: 'shopping_for',
    title: 'Who are you shopping for?',
    subtitle: 'Helps us calibrate suggestions.',
    multi: false,
    options: [
      { value: 'just_me', label: 'Just me' },
      { value: 'partner', label: 'Me and my partner' },
      { value: 'family', label: 'My family' },
      { value: 'kids', label: 'My kids' },
      { value: 'elderly', label: 'An elderly parent' }
    ]
  },
  {
    key: 'strictness',
    title: 'How strict do you want your verdicts?',
    subtitle: 'You can change this later.',
    multi: false,
    options: [
      { value: 'strict', label: 'Strict', desc: 'Flag anything questionable' },
      { value: 'balanced', label: 'Balanced', desc: 'Be honest, not perfect' },
      { value: 'relaxed', label: 'Relaxed', desc: 'Only flag obvious problems' }
    ]
  },
  {
    key: 'medical_diet_type',
    title: 'Are you following a specific medical or doctor-directed diet?',
    subtitle: 'This drives more accurate verdicts on food safety for your situation.',
    multi: false,
    options: [
      { value: 'liquid', label: 'Liquid diet', desc: 'Doctor-prescribed liquid-only plan' },
      { value: 'pre_surgery', label: 'Pre-surgery', desc: 'Preparing for a procedure' },
      { value: 'post_surgery', label: 'Post-surgery', desc: 'Reduced stomach capacity' },
      { value: 'glp1', label: 'GLP-1 medication', desc: 'Ozempic, Wegovy, Mounjaro, Zepbound, etc.' },
      { value: 'diabetic', label: 'Diabetic', desc: 'Watching glycemic impact' },
      { value: 'other', label: 'Other', desc: "We'll capture the details" },
      { value: 'none', label: 'None' }
    ]
  }
];

// GLP-1 branch sub-questions (shown only if medical_diet_type === 'glp1')
const GLP1_QUESTIONS = [
  {
    key: 'glp1_medication',
    title: 'Which GLP-1 medication are you on?',
    subtitle: 'Different drugs need different tuning.',
    multi: false,
    options: [
      { value: 'semaglutide', label: 'Semaglutide', desc: 'Ozempic / Wegovy / Rybelsus' },
      { value: 'tirzepatide', label: 'Tirzepatide', desc: 'Mounjaro / Zepbound' },
      { value: 'orforglipron', label: 'Orforglipron', desc: 'Foundayo (oral, 2026)' },
      { value: 'liraglutide', label: 'Liraglutide', desc: 'Saxenda / Victoza' },
      { value: 'dulaglutide', label: 'Dulaglutide', desc: 'Trulicity' },
      { value: 'not_sure', label: 'Not sure / prefer not to say' }
    ]
  },
  {
    key: 'glp1_titration_stage',
    title: 'Where are you in your dose?',
    subtitle: 'The first few weeks are usually the roughest.',
    multi: false,
    options: [
      { value: 'starting', label: 'Just started or still increasing my dose', desc: 'First 4–8 weeks — GI tolerance is lower' },
      { value: 'maintenance', label: 'On a stable maintenance dose', desc: 'Body has adjusted' }
    ]
  }
];

// Other branch (free-text)
const OTHER_QUESTION = {
  key: 'medical_diet_other_text',
  title: 'Tell us about your condition.',
  subtitle: "We'll pass this along on every scan. Verdicts fall back to general tuning until we add specific logic — always double-check with your care team.",
  freetext: true
};

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    goal: '',
    avoid_ingredients: [],
    eating_style: '',
    allergies: [],
    shopping_for: '',
    strictness: 'balanced',
    medical_diet_type: '',
    glp1_medication: '',
    glp1_titration_stage: '',
    medical_diet_other_text: ''
  });
  const [loading, setLoading] = useState(false);
  const { token, completeOnboarding } = useAuth();
  const navigate = useNavigate();

  // Build the dynamic flow based on current answers
  const flow = useMemo(() => {
    const list = [...BASE_QUESTIONS];
    if (answers.medical_diet_type === 'glp1') list.push(...GLP1_QUESTIONS);
    else if (answers.medical_diet_type === 'other') list.push(OTHER_QUESTION);
    return list;
  }, [answers.medical_diet_type]);

  const totalSteps = flow.length + 1; // +1 for final success
  const currentQ = flow[step];
  const isFinal = step >= flow.length;

  const handleSelect = (value) => {
    if (!currentQ) return;
    if (currentQ.multi) {
      setAnswers((prev) => {
        const arr = prev[currentQ.key] || [];
        return {
          ...prev,
          [currentQ.key]: arr.includes(value)
            ? arr.filter((v) => v !== value)
            : [...arr, value]
        };
      });
    } else {
      setAnswers((prev) => ({ ...prev, [currentQ.key]: value }));
    }
  };

  const handleFreeText = (value) => {
    if (!currentQ) return;
    setAnswers((prev) => ({ ...prev, [currentQ.key]: value }));
  };

  const isAnswered = () => {
    if (!currentQ) return true;
    const v = answers[currentQ.key];
    if (currentQ.freetext) return typeof v === 'string' && v.trim().length > 0;
    if (currentQ.multi) return Array.isArray(v) && v.length > 0;
    return !!v;
  };

  const handleNext = async () => {
    if (!isAnswered()) {
      toast.error(currentQ?.freetext ? 'Please add a short description' : 'Please pick at least one');
      return;
    }
    if (navigator.vibrate) navigator.vibrate(30);

    // If we're not at the last dynamic question yet, advance
    if (step < flow.length - 1) {
      setStep(step + 1);
      return;
    }

    // Submit
    setLoading(true);
    try {
      await axios.post(`${API_URL}/profile/onboarding`, answers, {
        withCredentials: true
      });
      setStep(flow.length); // final screen
      setTimeout(async () => {
        await completeOnboarding();
        setLoading(false); // Reset loading before navigation
        navigate('/home', { replace: true });
      }, 1800);
    } catch (error) {
      toast.error('Failed to save profile');
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text flex flex-col"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      data-testid="onboarding-screen"
    >
      <div className="max-w-lg mx-auto w-full flex-1 flex flex-col px-5">
        {/* Top bar: back + progress */}
        <div className="flex items-center gap-3 pt-2 pb-6">
          <button
            onClick={handleBack}
            disabled={step === 0 || isFinal}
            data-testid="onboarding-back-button"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text disabled:opacity-30 disabled:cursor-not-allowed tappable"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 h-1.5 bg-qc-surface rounded-full overflow-hidden">
            <div
              className="h-full bg-qc-green transition-all duration-500 ease-out"
              style={{ width: `${((Math.min(step, flow.length) + 1) / totalSteps) * 100}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-qc-text-dim tabular-nums" data-testid="onboarding-step-counter">
            {Math.min(step + 1, totalSteps)} / {totalSteps}
          </span>
        </div>

        {/* Question */}
        {!isFinal && currentQ && (
          <div key={currentQ.key} className="flex-1 flex flex-col animate-fade-up" data-testid={`onboarding-step-${currentQ.key}`}>
            <div className="mb-6">
              <h1 className="text-3xl sm:text-4xl font-black leading-tight tracking-tight mb-2 text-qc-text">
                {currentQ.title}
              </h1>
              <p className="text-base text-qc-text-dim">{currentQ.subtitle}</p>
            </div>

            {currentQ.freetext ? (
              <div className="flex-1">
                <textarea
                  value={answers[currentQ.key] || ''}
                  onChange={(e) => handleFreeText(e.target.value)}
                  placeholder="e.g., low FODMAP, celiac, EOE, PCOS…"
                  rows={5}
                  maxLength={300}
                  data-testid={`freetext-${currentQ.key}`}
                  className="w-full px-4 py-3 rounded-2xl bg-qc-surface border-2 border-qc-border text-qc-text placeholder:text-qc-text-faint focus:outline-none focus:border-qc-green text-base resize-none"
                />
                <p className="text-xs text-qc-text-faint mt-2 text-right">
                  {(answers[currentQ.key] || '').length} / 300
                </p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pb-6">
                {currentQ.options.map((opt) => {
                  const value = answers[currentQ.key];
                  const isSelected = currentQ.multi
                    ? Array.isArray(value) && value.includes(opt.value)
                    : value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelect(opt.value)}
                      data-testid={`option-${currentQ.key}-${opt.value}`}
                      className={`w-full text-left tappable px-5 py-4 rounded-2xl border-2 flex items-center justify-between ${
                        isSelected
                          ? 'bg-qc-green/10 border-qc-green'
                          : 'bg-qc-surface border-qc-border hover:border-qc-text-faint'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-base font-bold text-qc-text">{opt.label}</div>
                        {opt.desc && <div className="text-sm text-qc-text-dim mt-0.5">{opt.desc}</div>}
                      </div>
                      <div
                        className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center border-2 transition-all ${
                          isSelected ? 'bg-qc-green border-qc-green' : 'border-qc-border'
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-qc-bg" strokeWidth={3} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Final success screen */}
        {isFinal && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-2 animate-fade-up" data-testid="onboarding-success-screen">
            <div className="relative mb-8">
              <div className="absolute inset-0 rounded-full bg-qc-green/20 blur-2xl animate-green-pulse" />
              <div className="relative w-28 h-28 rounded-full bg-qc-green flex items-center justify-center shadow-[0_0_60px_rgba(0,230,118,0.55)]">
                <svg viewBox="0 0 52 52" className="w-14 h-14" fill="none">
                  <path
                    d="M14 27 L23 36 L40 18"
                    stroke="#0A0A0A"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="60"
                    strokeDashoffset="60"
                    style={{ animation: 'check-draw 0.6s ease-out 0.2s forwards' }}
                  />
                </svg>
              </div>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-qc-text mb-3">
              Your profile is ready.
            </h1>
            <p className="text-base text-qc-text-dim max-w-xs">Let&apos;s shop smarter.</p>
          </div>
        )}

        {/* Footer / Continue button */}
        {!isFinal && (
          <div
            className="pt-4 pb-2 sticky bottom-0 bg-gradient-to-t from-qc-bg via-qc-bg to-transparent"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))' }}
          >
            <Button
              onClick={handleNext}
              disabled={loading || !isAnswered()}
              data-testid="onboarding-continue-button"
              className="w-full h-14 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-2xl font-extrabold text-base shadow-[0_8px_24px_rgba(0,230,118,0.3)] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {step === flow.length - 1 ? (loading ? 'Saving...' : 'Finish') : 'Continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
